import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Select, Button, message, DatePicker, Tabs, Badge, Popconfirm, Tooltip, Tag, Empty, Spin, Modal } from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import {
    getClients, getTaxInvoiceData, downloadTaxInvoicePdf, getTaxInvoiceFilters,
    getSavedTaxInvoices, saveTaxInvoice, updateSavedTaxInvoice, deleteSavedTaxInvoice
} from '../api';
import { PrinterOutlined, DownloadOutlined, DeleteOutlined, PlusOutlined, SaveOutlined, ReloadOutlined, FolderOpenOutlined, CloudOutlined } from '@ant-design/icons';
import { useReactToPrint } from 'react-to-print';

const months = Array.from({ length: 12 }, (_, i) => ({ label: moment().month(i).format('MMMM'), value: i + 1 }));
const years = Array.from({ length: 8 }, (_, i) => ({ label: String(moment().year() - 3 + i), value: moment().year() - 3 + i }));

const formatDate = (date) => date ? dayjs(date).format('DD/MM/YYYY') : '';

// Helper to calculate totals
const calculateTotals = (group) => {
    let totalExcl = 0;
    let totalVat = 0;
    const depts = group.departments || group.departments_data || [];
    depts.forEach(dept => {
        if (dept.is_subject) return;
        const hours = parseFloat(dept.hours) || 0;
        const rate = parseFloat(dept.rate) || 0;
        const vat = parseFloat(dept.vat) || 0;
        const amount = hours * rate;
        const vatAmount = amount * (vat / 100);
        totalExcl += amount;
        totalVat += vatAmount;
    });
    return { totalExcl, totalVat, totalIncl: totalExcl + totalVat };
};

// Sub-component for Saved Invoices to avoid unmounting on parent state change
const SavedInvoicesPanel = ({
    savedInvoices,
    savedLoading,
    filters,
    months,
    loadSavedInvoices,
    handleLoadSaved,
    handleDownloadPdf,
    setPrintData,
    handleDeleteSaved
}) => {
    // Helper: reconstruct a group object from a saved invoice record
    const buildGroupFromSaved = (inv) => ({
        client_name: inv.client_name,
        header: {
            ...inv.header_data,
            date: inv.header_data.date ? dayjs(inv.header_data.date) : dayjs(),
        },
        departments: inv.departments_data,
    });

    return (
        <div style={{ paddingTop: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <span style={{ color: '#888', fontSize: 13 }}>
                    Saved invoices for {months.find(m => m.value === filters.month)?.label} {filters.year}
                </span>
                <Button icon={<ReloadOutlined />} size="small" onClick={loadSavedInvoices} loading={savedLoading}>
                    Refresh
                </Button>
            </div>

            {savedLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            ) : savedInvoices.length === 0 ? (
                <Empty description="No saved invoices for this month/year" />
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {savedInvoices.map(inv => {
                        const totals = calculateTotals(inv);
                        return (
                            <Card
                                key={inv.id}
                                size="small"
                                style={{ border: '1px solid #e8e8e8', borderRadius: 8 }}
                                bodyStyle={{ padding: '12px 16px' }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 15 }}>{inv.client_name}</div>
                                        <div style={{ color: '#888', fontSize: 12, marginTop: 2 }}>
                                            Invoice No: <b>{inv.invoice_no || '–'}</b> &nbsp;|&nbsp;
                                            Date: <b>{inv.invoice_date ? dayjs(inv.invoice_date).format('DD/MM/YYYY') : '–'}</b> &nbsp;|&nbsp;
                                            Total: <b>AED {totals.totalIncl.toFixed(2)}</b>
                                        </div>
                                        <div style={{ marginTop: 4 }}>
                                            <Tag color="blue">{months.find(m => m.value === inv.month)?.label} {inv.year}</Tag>
                                            <Tag color={inv.status === 'saved' ? 'default' : inv.status === 'printed' ? 'green' : 'orange'}>
                                                {inv.status}
                                            </Tag>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <Tooltip title="Load & Edit">
                                            <Button
                                                icon={<FolderOpenOutlined />}
                                                size="small"
                                                type="primary"
                                                ghost
                                                onClick={() => handleLoadSaved(inv)}
                                            >
                                                Load
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Download PDF">
                                            <Button
                                                icon={<DownloadOutlined />}
                                                size="small"
                                                onClick={() => handleDownloadPdf(buildGroupFromSaved(inv))}
                                            />
                                        </Tooltip>
                                        <Tooltip title="Print">
                                            <Button
                                                icon={<PrinterOutlined />}
                                                size="small"
                                                onClick={() => setPrintData(buildGroupFromSaved(inv))}
                                            />
                                        </Tooltip>
                                        <Popconfirm
                                            title="Delete this saved invoice?"
                                            onConfirm={() => handleDeleteSaved(inv.id)}
                                            okText="Delete"
                                            okButtonProps={{ danger: true }}
                                        >
                                            <Tooltip title="Delete">
                                                <Button icon={<DeleteOutlined />} size="small" danger />
                                            </Tooltip>
                                        </Popconfirm>
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// Invoice Component that serves as both Editor and Print View
const InvoicePaper = React.forwardRef(({ data, header, onUpdateHeader, onUpdateDepartment, onAddRow, onRemoveRow, readOnly = false }, ref) => {
    const pickerOpenCountRef = useRef(0);
    const pickerScrollHandlerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (pickerScrollHandlerRef.current) {
                document.removeEventListener('wheel', pickerScrollHandlerRef.current, true);
                document.removeEventListener('touchmove', pickerScrollHandlerRef.current, true);
                pickerScrollHandlerRef.current = null;
                pickerOpenCountRef.current = 0;
            }
        };
    }, []);

    const blockPickerScroll = (open) => {
        if (!pickerScrollHandlerRef.current) {
            pickerScrollHandlerRef.current = (e) => {
                const t = e.target;
                if (t && (t.closest('.ant-picker-dropdown') || t.closest('.ant-picker'))) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            };
        }

        if (open) {
            pickerOpenCountRef.current += 1;
            if (pickerOpenCountRef.current === 1) {
                document.addEventListener('wheel', pickerScrollHandlerRef.current, { passive: false, capture: true });
                document.addEventListener('touchmove', pickerScrollHandlerRef.current, { passive: false, capture: true });
            }
            return;
        }

        pickerOpenCountRef.current = Math.max(0, pickerOpenCountRef.current - 1);
        if (pickerOpenCountRef.current === 0 && pickerScrollHandlerRef.current) {
            document.removeEventListener('wheel', pickerScrollHandlerRef.current, true);
            document.removeEventListener('touchmove', pickerScrollHandlerRef.current, true);
        }
    };

    // Always render the container div so the Ref remains stable
    return (
        <div ref={ref} style={{
            padding: '40px',
            background: 'white',
            color: '#000',
            fontFamily: 'Arial, sans-serif',
            width: '100%',
            maxWidth: '800px',
            margin: '0 auto',
            boxShadow: readOnly ? 'none' : '0 0 10px rgba(0,0,0,0.1)',
            minHeight: '1000px',
            position: 'relative',
            // If no data, hide the inner content but keep the div for the Ref
            display: data ? 'block' : 'none'
        }}>
            {!data ? null : (() => {
                const totalExcl = data.departments.reduce((sum, d) => sum + (d.hours * d.rate), 0);
                const totalVat = data.departments.reduce((sum, d) => sum + ((d.hours * d.rate) * d.vat / 100), 0);
                const totalIncl = totalExcl + totalVat;

                const inputStyle = {
                    border: readOnly ? 'none' : '1px dashed #ccc',
                    background: 'transparent',
                    padding: '2px 4px',
                    width: '100%',
                    color: '#000000',
                    fontWeight: 'bold',
                    fontSize: 'inherit',
                    fontFamily: 'inherit'
                };

                return (
                    <>
                        <style>
                            {`
                          @media print {
                            @page { margin: 10mm; }
                            body { -webkit-print-color-adjust: exact; }
                            input, textarea { border: none !important; }
                          }
                          .invoice-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                          .invoice-table th { border: 2px solid #000; padding: 4px; text-align: center; color: #000000; font-weight: bold; }
                          .invoice-table td { border: 2px solid #000; padding: 4px; text-align: center; vertical-align: top; }
                          .blue-text { color: #000000; font-weight: bold; }
                          .editable-field:focus { outline: 1px solid #1890ff; background: #e6f7ff; }
                        `}
                        </style>

                        <div style={{ textAlign: 'right', marginTop: 20, marginBottom: 10 }}>
                            <div className="blue-text">
                                TRN : <input
                                    style={{ ...inputStyle, width: 150, display: 'inline-block' }}
                                    value={header.myTrn}
                                    onChange={e => onUpdateHeader && onUpdateHeader('myTrn', e.target.value)}
                                    readOnly={readOnly}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                            <div style={{ width: '50%' }}>
                                <div className="blue-text" style={{ fontSize: 16, marginBottom: 4 }}>
                                    {data.client_name && !data.client_name.toUpperCase().includes('M/S') ? 'M/s. ' : ''}{data.client_name}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                    {header.poBoxLabel && <span style={{ whiteSpace: 'nowrap', marginRight: 4 }}>{header.poBoxLabel}:</span>}
                                    <input style={{ ...inputStyle, width: '100%', color: '#000', fontWeight: 'normal' }} value={header.poBox} onChange={e => onUpdateHeader('poBox', e.target.value)} readOnly={readOnly} />
                                </div>
                                <div style={{ marginTop: 2 }}>
                                    <textarea
                                        style={{ ...inputStyle, width: '100%', color: '#000', fontWeight: 'normal', height: 'auto', minHeight: 40, resize: 'none' }}
                                        value={header.city}
                                        onChange={e => onUpdateHeader('city', e.target.value)}
                                        readOnly={readOnly}
                                        rows={2}
                                    />
                                </div>
                                <div style={{ marginTop: 10 }} className="blue-text">
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        TRN : <input style={{ ...inputStyle, width: 150 }} value={header.clientTrn} onChange={e => onUpdateHeader('clientTrn', e.target.value)} readOnly={readOnly} />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        Emirate: <input style={{ ...inputStyle, width: 100 }} value={header.clientEmirate} onChange={e => onUpdateHeader('clientEmirate', e.target.value)} readOnly={readOnly} />.
                                    </div>
                                </div>
                            </div>
                            <div style={{ width: '40%' }}>
                                <div style={{ display: 'flex', margin: '4px 0', alignItems: 'center' }}>
                                    <span className="blue-text" style={{ width: 100 }}>Invoice No :</span>
                                    <input style={inputStyle} value={header.invoiceNo} onChange={e => onUpdateHeader('invoiceNo', e.target.value)} readOnly={readOnly} />
                                </div>
                                <div style={{ display: 'flex', margin: '4px 0', alignItems: 'center' }}>
                                    <span className="blue-text" style={{ width: 100 }}>Date :</span>
                                    {readOnly ? (
                                        <span className="blue-text" style={{ flex: 1, padding: '2px 4px' }}>{formatDate(header.date)}</span>
                                    ) : (
                                        <DatePicker
                                            value={header?.date ? dayjs(header.date) : null}
                                            onChange={d => onUpdateHeader('date', d)}
                                            format="DD/MM/YYYY"
                                            bordered={false}
                                            style={{ ...inputStyle, width: '100%', padding: 0 }}
                                            allowClear={false}
                                            inputReadOnly={true}
                                            onOpenChange={blockPickerScroll}
                                        />
                                    )}
                                </div>
                                <div style={{ display: 'flex', margin: '4px 0', alignItems: 'center' }}>
                                    <span className="blue-text" style={{ width: 100 }}>L P O NO :</span>
                                    <input style={inputStyle} value={header.lpo} onChange={e => onUpdateHeader('lpo', e.target.value)} readOnly={readOnly} />
                                </div>
                                <div style={{ display: 'flex', margin: '4px 0', alignItems: 'center' }}>
                                    <span className="blue-text" style={{ width: 100 }}>Payment :</span>
                                    <input style={inputStyle} value={header.paymentTerms} onChange={e => onUpdateHeader('paymentTerms', e.target.value)} readOnly={readOnly} />
                                </div>
                            </div>
                        </div>

                        <table className="invoice-table">
                            <thead>
                                <tr>
                                    <th className="blue-text">SN</th>
                                    <th className="blue-text" style={{ width: '30%' }}>Description</th>
                                    <th className="blue-text">No.of<br />hrs.</th>
                                    <th className="blue-text">Rate<br />/hr</th>
                                    <th className="blue-text">Amount<br />Excl VAT<br />(AED)</th>
                                    <th className="blue-text">VAT<br />%</th>
                                    <th className="blue-text">VAT<br />Amount</th>
                                    <th className="blue-text">Amount<br />Incl VAT<br />(AED)</th>
                                    {!readOnly && <th className="blue-text" style={{ width: 40, borderLeft: 'none' }}></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    let currentSn = 1;
                                    return data.departments.map((dept, idx) => {
                                        const snValue = dept.is_subject ? '' : currentSn++;
                                        return (
                                            <tr key={idx}>
                                                <td className="blue-text">{snValue}</td>
                                                <td style={{ textAlign: 'left' }} className="blue-text">
                                                    {readOnly ? (
                                                        <div style={{ whiteSpace: 'pre-wrap' }}>{dept.description}</div>
                                                    ) : (
                                                        <textarea
                                                            style={{ ...inputStyle, height: 'auto', minHeight: dept.is_subject ? 30 : 60, resize: 'none' }}
                                                            value={dept.description}
                                                            onChange={e => onUpdateDepartment(idx, 'description', e.target.value)}
                                                            rows={dept.is_subject ? 2 : 4}
                                                        />
                                                    )}
                                                </td>
                                                {dept.is_subject ? (
                                                    <>
                                                        <td></td>
                                                        <td></td>
                                                        <td></td>
                                                        <td></td>
                                                        <td></td>
                                                        <td></td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="blue-text">
                                                            <input
                                                                type="number"
                                                                style={{ ...inputStyle, textAlign: 'center' }}
                                                                value={dept.hours}
                                                                onChange={e => onUpdateDepartment(idx, 'hours', e.target.value)}
                                                                readOnly={readOnly}
                                                            />
                                                        </td>
                                                        <td className="blue-text">
                                                            <input
                                                                type="number"
                                                                style={{ ...inputStyle, textAlign: 'center' }}
                                                                value={dept.rate}
                                                                onChange={e => onUpdateDepartment(idx, 'rate', e.target.value)}
                                                                step="0.01"
                                                                readOnly={readOnly}
                                                            />
                                                        </td>
                                                        <td className="blue-text">{(Number(dept.hours) * Number(dept.rate)).toFixed(2)}</td>
                                                        <td className="blue-text">
                                                            <input
                                                                type="number"
                                                                style={{ ...inputStyle, textAlign: 'center' }}
                                                                value={dept.vat}
                                                                onChange={e => onUpdateDepartment(idx, 'vat', e.target.value)}
                                                                readOnly={readOnly}
                                                            />%
                                                        </td>
                                                        <td className="blue-text">{((Number(dept.hours) * Number(dept.rate)) * dept.vat / 100).toFixed(2)}</td>
                                                        <td className="blue-text">{((Number(dept.hours) * Number(dept.rate)) * (1 + dept.vat / 100)).toFixed(2)}</td>
                                                    </>
                                                )}
                                                {!readOnly && (
                                                    <td style={{ verticalAlign: 'middle', borderLeft: 'none' }}>
                                                        <Button
                                                            size="small"
                                                            type="text"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => onRemoveRow && onRemoveRow(idx)}
                                                        />
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })
                                }
                                )()}
                                {!readOnly && (
                                    <tr>
                                        <td colSpan={!readOnly ? 9 : 8} style={{ textAlign: 'center' }}>
                                            <Button type="dashed" icon={<PlusOutlined />} onClick={onAddRow} style={{ width: '100%' }}>+ Add Row</Button>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={4} style={{ textAlign: 'right' }}></td>
                                    <td className="blue-text" style={{ fontWeight: 'bold' }}>{totalExcl.toFixed(2)}</td>
                                    <td></td>
                                    <td className="blue-text" style={{ fontWeight: 'bold' }}>{totalVat.toFixed(2)}</td>
                                    <td className="blue-text" style={{ fontWeight: 'bold' }}>{totalIncl.toFixed(2)}</td>
                                    {!readOnly && <td style={{ border: 'none' }}></td>}
                                </tr>
                            </tfoot>
                        </table>


                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                            <table style={{ borderCollapse: 'collapse', width: '300px' }}>
                                <tbody>
                                    <tr>
                                        <td className="blue-text" style={{ textAlign: 'right', fontWeight: 'bold', border: 'none', padding: 4 }}>Total Before VAT</td>
                                        <td className="blue-text" style={{ textAlign: 'right', fontWeight: 'bold', border: 'none', padding: 4 }}>{totalExcl.toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td className="blue-text" style={{ textAlign: 'right', fontWeight: 'bold', border: 'none', padding: 4 }}>VAT 5%</td>
                                        <td className="blue-text" style={{ textAlign: 'right', fontWeight: 'bold', border: 'none', padding: 4 }}>{totalVat.toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td className="blue-text" style={{ textAlign: 'right', fontWeight: 'bold', border: 'none', padding: 4 }}>Total</td>
                                        <td className="blue-text" style={{ textAlign: 'right', fontWeight: 'bold', border: 'none', padding: 4 }}>{totalIncl.toFixed(2)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div style={{ marginTop: 10, borderTop: '2px solid navy', fontSize: '10px' }}>
                            <div className="blue-text" style={{ fontWeight: 'bold', margin: '4px 0' }}>Total Vat: AED {convertNumberToEnglish(totalVat)} only.</div>
                            <div className="blue-text" style={{ fontWeight: 'bold', margin: '4px 0' }}>Total Amount: AED {convertNumberToEnglish(totalIncl)} only.</div>
                        </div>

                        {(() => {
                            const isMitsubishi = data.client_name && data.client_name.toUpperCase().includes('MITSUBISHI ELECTRIC');
                            return (
                                <>
                                    <div style={{ marginTop: 15, border: '2px solid black', display: 'inline-block', padding: '10px' }}>
                                        <div style={{ fontWeight: 'bold' }}>Account Name: {isMitsubishi ? 'DEBBA ELEVATOR MAINT & FIX.' : 'DEBA TECH CONT.'}</div>
                                        <div style={{ fontWeight: 'bold' }}>A/c No: {isMitsubishi ? 'AE100400000572423401001' : 'AE290400000192361986001'}</div>
                                        <div style={{ fontWeight: 'bold' }}>Bank: RAK BANK</div>
                                    </div>

                                    <div style={{ position: 'relative', marginTop: 10, textAlign: 'right' }} className="blue-text">
                                        <b>For {isMitsubishi ? 'DEBBA ELEVATOR MAINT & FIX.' : 'Deba Technical Cont.'}</b>
                                    </div>
                                </>
                            );
                        })()}

                    </>
                );
            })()}
        </div>
    );
});

// Helper for number to words
function convertNumberToEnglish(n) {
    if (n < 0) return false;
    const single_digit = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const double_digit = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const below_hundred = ['Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    if (n === 0) return 'Zero';

    function translate(n) {
        let word = "";
        if (n < 10) { word = single_digit[n] + ' '; }
        else if (n < 20) { word = double_digit[n - 10] + ' '; }
        else if (n < 100) {
            let rem = translate(n % 10);
            word = below_hundred[Math.floor(n / 10) - 2] + ' ' + rem;
        }
        else if (n < 1000) {
            word = single_digit[Math.floor(n / 100)] + ' Hundred ' + translate(n % 100);
        }
        else if (n < 1000000) {
            word = translate(Math.floor(n / 1000)).trim() + ' Thousand ' + translate(n % 1000);
        }
        else if (n < 1000000000) {
            word = translate(Math.floor(n / 1000000)).trim() + ' Million ' + translate(n % 1000000);
        }
        else { word = translate(Math.floor(n / 1000000000)).trim() + ' Billion ' + translate(n % 1000000000); }
        return word;
    }

    let result = translate(Math.floor(n));
    let decimals = Math.round((n % 1) * 100);

    if (decimals > 0) {
        return result.trim() + ' and ' + translate(decimals).trim() + ' Fils';
    }
    return result.trim();
}

// ──────────────────────────────────────────────────────────
//  Main Component
// ──────────────────────────────────────────────────────────
const TaxInvoice = () => {
    const now = moment();
    const [filters, setFilters] = useState({ month: now.month() + 1, year: now.year(), groupBy: 'department' });

    // Master data
    const [allClientsFilter, setAllClientsFilter] = useState([]);
    const [availableDepartments, setAvailableDepartments] = useState([]);
    const [availableSkills, setAvailableSkills] = useState([]);

    // Selection keys
    const [selectedClients, setSelectedClients] = useState([]);
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedSkills, setSelectedSkills] = useState([]);

    const [invoiceGroups, setInvoiceGroups] = useState([]);
    const [loading, setLoading] = useState(false);

    // Saved Invoices panel
    const [savedInvoices, setSavedInvoices] = useState([]);
    const [savedLoading, setSavedLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('generate');
    const [savingGroupIdx, setSavingGroupIdx] = useState(null);

    // ── Print ──
    const [printData, setPrintData] = useState(null);
    const hiddenPrintRef = useRef();
    const printTriggerRef = useRef(null);

    const triggerPrint = useReactToPrint({
        contentRef: hiddenPrintRef,
        onAfterPrint: () => {
            console.log('Print completed or cancelled');
            setPrintData(null);
        }
    });

    // Store the latest triggerPrint in a ref so the effect always has the current version
    printTriggerRef.current = triggerPrint;

    useEffect(() => {
        if (printData) {
            // Give React time to render the hidden InvoicePaper with the new printData
            const timer = setTimeout(() => {
                console.log('Initiating print trigger', {
                    hasContent: !!hiddenPrintRef.current,
                    hasTrigger: !!printTriggerRef.current
                });
                if (hiddenPrintRef.current && printTriggerRef.current) {
                    printTriggerRef.current();
                } else {
                    console.warn('Print canceled: trigger or ref missing');
                    setPrintData(null);
                }
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [printData]);

    const loadFilters = async () => {
        try {
            const res = await getTaxInvoiceFilters({ month: filters.month, year: filters.year });
            if (res.success) {
                setAllClientsFilter(res.data.clients || []);
                setAvailableDepartments(res.data.departments || []);
                setAvailableSkills(res.data.skills || []);
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        loadFilters();
    }, [filters.month, filters.year]);

    // Load saved invoices whenever we switch to saved tab or month/year changes
    const loadSavedInvoices = async () => {
        setSavedLoading(true);
        try {
            const res = await getSavedTaxInvoices({ month: filters.month, year: filters.year });
            if (res.success) setSavedInvoices(res.data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setSavedLoading(false);
        }
    };

    useEffect(() => {
        loadSavedInvoices();
        // eslint-disable-next-line
    }, [activeTab, filters.month, filters.year]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = { ...filters, clients: selectedClients, departments: selectedDepartments, skills: selectedSkills, groupBy: filters.groupBy };
            const res = await getTaxInvoiceData(params);
            if (res.success) {
                const groups = res.data.map(g => {
                    const clientNameUpper = (g.client_name || '').toUpperCase();
                    const isAlFuttaim = clientNameUpper.includes('AL FUTTAIM');
                    const isCorodex = clientNameUpper.includes('CORODEX');
                    const isBmts = clientNameUpper.includes('BMTS') || clientNameUpper.includes('BAHRI') || clientNameUpper.includes('MAZROEI');
                    const isAkcura = clientNameUpper.includes('AKCURA');
                    const isAccess1 = clientNameUpper.includes('ACCESS');

                    let poBox = '1247';
                    let city = 'Dubai, U A E';
                    let clientTrn = '100254605700003';
                    let paymentTerms = '60 Days';
                    let poBoxLabel = 'PO.BOX';

                    if (isAlFuttaim) {
                        g.client_name = 'Al Futtaim Engineering LLC';
                        poBox = '159';
                        city = 'DUBAI UAE';
                        clientTrn = '100047128200003';
                        paymentTerms = '30 Days';
                        poBoxLabel = 'PO.BOX';
                    } else if (isCorodex) {
                        g.client_name = 'Corodex Agencies Co. L.L.C';
                        poBox = '12282';
                        city = 'Dubai ,U A E';
                        clientTrn = '100547779700003';
                        paymentTerms = '60 Days';
                        poBoxLabel = 'Post Box no';
                    } else if (isBmts) {
                        g.client_name = 'Bahri & Mazroei Technical Syatem Co.';
                        poBox = '1247';
                        city = 'Dubai ,U A E';
                        clientTrn = '100254605700003';
                        paymentTerms = '60 Days';
                        poBoxLabel = 'Post Box no';
                    } else if (isAkcura) {
                        g.client_name = 'Akcura MetalCrafters';
                        poBox = '236995';
                        city = 'Dubai, UAE.';
                        clientTrn = '100339770800003';
                        paymentTerms = '60 Days';
                        poBoxLabel = 'P.O.Box.';
                    } else if (isAccess1) {
                        g.client_name = 'Access 01';
                        poBox = 'European Business Centre';
                        city = 'Dubai Investment Park 1\nDubai, UAE.';
                        clientTrn = '100338008400003';
                        paymentTerms = '60 Days';
                        poBoxLabel = '';
                    } else if (clientNameUpper.includes('MITSUBISHI ELECTRIC')) {
                        g.client_name = 'M/s. Mitsubishi Electric Middle East Africa and CIS Co. L.L.C.';
                    }

                    return {
                        ...g,
                        header: {
                            myTrn: '104060472800003',
                            poBox,
                            poBoxLabel,
                            city,
                            clientTrn,
                            clientEmirate: 'Dubai',
                            invoiceNo: `INV-${moment().format('YYYYMM')}-${Math.floor(Math.random() * 1000)}`,
                            date: dayjs(),
                            lpo: '',
                            paymentTerms
                        },
                        departments: g.departments.map(d => ({
                            ...d,
                            hours: d.total_hours,
                            rate: 0,
                            vat: 5,
                        }))
                    };
                });
                setInvoiceGroups(groups);
                if (groups.length === 0) message.info('No timesheet data found for selected criteria');
            }
        } catch (e) {
            console.error(e);
            message.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };




    // ── Download PDF ──
    const handleDownloadPdf = async (group) => {
        try {
            message.loading({ content: 'Generating PDF...', key: 'pdf_download' });
            let invoicesToDownload = group ? [group] : invoiceGroups;
            invoicesToDownload = invoicesToDownload.map(inv => {
                const totals = calculateTotals(inv);
                return {
                    ...inv,
                    totalVatWords: convertNumberToEnglish(totals.totalVat),
                    totalAmountWords: convertNumberToEnglish(totals.totalIncl)
                };
            });
            await downloadTaxInvoicePdf(invoicesToDownload);
            message.success({ content: 'PDF Downloaded', key: 'pdf_download' });
        } catch (e) {
            console.error(e);
            message.error({ content: 'Failed to download PDF', key: 'pdf_download' });
        }
    };

    // ── Save invoice ──
    const handleSaveInvoice = async (group, groupIndex) => {
        setSavingGroupIdx(groupIndex);
        try {
            const payload = {
                client_name: group.client_name,
                month: filters.month,
                year: filters.year,
                invoice_no: group.header.invoiceNo,
                invoice_date: group.header.date ? dayjs(group.header.date).format('YYYY-MM-DD') : null,
                header_data: {
                    ...group.header,
                    date: group.header.date ? dayjs(group.header.date).format('YYYY-MM-DD') : null,
                },
                departments_data: group.departments,
                status: 'saved',
            };

            let res;
            if (group._savedId) {
                // Update existing
                res = await updateSavedTaxInvoice(group._savedId, payload);
            } else {
                // Create new
                res = await saveTaxInvoice(payload);
            }

            if (res.success) {
                message.success(`Invoice for ${group.client_name} ${group._savedId ? 'updated' : 'saved'} successfully!`);

                // If it was a new save, capture the ID so subsequent saves are updates
                if (!group._savedId && res.data && res.data.id) {
                    setInvoiceGroups(prev => {
                        const next = [...prev];
                        next[groupIndex] = { ...next[groupIndex], _savedId: res.data.id };
                        return next;
                    });
                }

                loadSavedInvoices();
            } else {
                message.error(res.message || 'Failed to save invoice');
            }
        } catch (e) {
            console.error(e);
            message.error('Failed to save invoice');
        } finally {
            setSavingGroupIdx(null);
        }
    };

    // ── Load a saved invoice into the generator ──
    const handleLoadSaved = (saved) => {
        const restoredGroup = {
            client_name: saved.client_name,
            header: {
                ...saved.header_data,
                date: saved.header_data.date ? dayjs(saved.header_data.date) : dayjs(),
            },
            departments: JSON.parse(JSON.stringify(saved.departments_data || [])),
            _savedId: saved.id,
        };
        // Replace current list with the loaded one for editing
        setInvoiceGroups([restoredGroup]);
        setActiveTab('generate');
        message.success(`Loaded: ${saved.client_name}`);
    };

    // ── Delete saved invoice ──
    const handleDeleteSaved = async (id) => {
        try {
            const res = await deleteSavedTaxInvoice(id);
            if (res.success) {
                message.success('Saved invoice deleted');
                loadSavedInvoices();
            } else {
                message.error(res.message || 'Failed to delete');
            }
        } catch (e) {
            message.error('Failed to delete');
        }
    };

    // ── Group editor helpers ──
    const updateDepartment = (groupIndex, deptIndex, field, value) => {
        setInvoiceGroups(prev => {
            const next = [...prev];
            const targetGroup = { ...next[groupIndex] };
            const nextDepts = [...targetGroup.departments];
            nextDepts[deptIndex] = { ...nextDepts[deptIndex], [field]: value };
            targetGroup.departments = nextDepts;
            next[groupIndex] = targetGroup;
            return next;
        });
    };

    const updateHeader = (groupIndex, field, value) => {
        setInvoiceGroups(prev => {
            const next = [...prev];
            next[groupIndex] = {
                ...next[groupIndex],
                header: {
                    ...next[groupIndex].header,
                    [field]: value
                }
            };
            return next;
        });
    };

    const addRow = (groupIndex) => {
        setInvoiceGroups(prev => {
            const next = [...prev];
            const targetGroup = { ...next[groupIndex] };
            targetGroup.departments = [
                ...targetGroup.departments,
                { description: '', hours: 0, rate: 0, vat: 5, is_subject: false }
            ];
            next[groupIndex] = targetGroup;
            return next;
        });
    };

    const removeRow = (groupIndex, deptIndex) => {
        setInvoiceGroups(prev => {
            const next = [...prev];
            const targetGroup = { ...next[groupIndex] };
            targetGroup.departments = targetGroup.departments.filter((_, i) => i !== deptIndex);
            next[groupIndex] = targetGroup;
            return next;
        });
    };



    // ──────────────────────────────────────────────────────────
    return (
        <div style={{ padding: 24 }}>
            <Card
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span>Tax Invoice Report</span>
                        {savedInvoices.length > 0 && (
                            <Badge count={savedInvoices.length} size="small" style={{ backgroundColor: '#1890ff' }} />
                        )}
                    </div>
                }
            >
                {/* Filters Row */}
                <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                    <Form.Item label="Month" style={{ margin: 0 }}>
                        <Select value={filters.month} style={{ width: 120 }} options={months} onChange={v => setFilters({ ...filters, month: v })} />
                    </Form.Item>
                    <Form.Item label="Year" style={{ margin: 0 }}>
                        <Select value={filters.year} style={{ width: 100 }} options={years} onChange={v => setFilters({ ...filters, year: v })} />
                    </Form.Item>
                    <Form.Item label="Group By" style={{ margin: 0 }}>
                        <Select
                            value={filters.groupBy}
                            style={{ width: 130 }}
                            onChange={v => setFilters({ ...filters, groupBy: v })}
                            options={[
                                { label: 'Department', value: 'department' },
                                { label: 'Skills', value: 'skill' }
                            ]}
                        />
                    </Form.Item>
                    <Form.Item label="Clients" style={{ margin: 0, minWidth: 200 }}>
                        <Select
                            mode="multiple"
                            placeholder="All Clients"
                            allowClear
                            style={{ minWidth: 200, maxWidth: 400 }}
                            options={allClientsFilter.map(c => ({ label: c, value: c }))}
                            value={selectedClients}
                            onChange={setSelectedClients}
                        />
                    </Form.Item>
                    <Form.Item label="Departments" style={{ margin: 0, minWidth: 200 }}>
                        <Select
                            mode="multiple"
                            placeholder="All Departments"
                            allowClear
                            style={{ minWidth: 200, maxWidth: 400 }}
                            options={availableDepartments.map(d => ({ label: d, value: d }))}
                            value={selectedDepartments}
                            onChange={setSelectedDepartments}
                        />
                    </Form.Item>
                    <Form.Item label="Skills" style={{ margin: 0, minWidth: 200 }}>
                        <Select
                            mode="multiple"
                            placeholder="All Skills"
                            allowClear
                            style={{ minWidth: 200, maxWidth: 400 }}
                            options={availableSkills.map(s => ({ label: s, value: s }))}
                            value={selectedSkills}
                            onChange={setSelectedSkills}
                        />
                    </Form.Item>
                    <Button type="primary" onClick={fetchData} loading={loading}>Generate</Button>
                    {invoiceGroups.length > 0 && (
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownloadPdf(null)}
                        >
                            Download All
                        </Button>
                    )}
                </div>

                {/* Tabs: Generate / Saved */}
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: 'generate',
                            label: (
                                <span>
                                    Generate
                                    {invoiceGroups.length > 0 && (
                                        <Badge count={invoiceGroups.length} size="small" style={{ marginLeft: 6, backgroundColor: '#52c41a' }} />
                                    )}
                                </span>
                            ),
                            children: (
                                <>
                                    {invoiceGroups.length === 0 && !loading && (
                                        <div style={{ textAlign: 'center', color: '#888', marginTop: 40 }}>
                                            Select filters and click Generate. If no data appears, ensure Timesheets are uploaded for this month.
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
                                        {invoiceGroups.map((group, groupIndex) => (
                                            <Card
                                                key={groupIndex}
                                                title={
                                                    <span>
                                                        {group.client_name}
                                                        {group._savedId && <Tag color="blue" style={{ marginLeft: 8 }}>Loaded from saved</Tag>}
                                                    </span>
                                                }
                                                extra={
                                                    <div style={{ display: 'flex', gap: 8 }}>
                                                        <Tooltip title="Save this invoice to database">
                                                            <Button
                                                                icon={<SaveOutlined />}
                                                                type="primary"
                                                                onClick={() => handleSaveInvoice(group, groupIndex)}
                                                                loading={savingGroupIdx === groupIndex}
                                                            >
                                                                Save
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip title="Download as PDF">
                                                            <Button icon={<DownloadOutlined />} onClick={() => handleDownloadPdf(group)}>Download</Button>
                                                        </Tooltip>
                                                        <Tooltip title="Print this invoice">
                                                            <Button icon={<PrinterOutlined />} onClick={() => setPrintData(group)}>Print</Button>
                                                        </Tooltip>
                                                    </div>
                                                }
                                            >
                                                <InvoicePaper
                                                    data={group}
                                                    header={group.header}
                                                    onUpdateHeader={(f, v) => updateHeader(groupIndex, f, v)}
                                                    onUpdateDepartment={(i, f, v) => updateDepartment(groupIndex, i, f, v)}
                                                    onAddRow={() => addRow(groupIndex)}
                                                    onRemoveRow={(i) => removeRow(groupIndex, i)}
                                                />
                                            </Card>
                                        ))}
                                    </div>
                                </>
                            ),
                        },
                        {
                            key: 'saved',
                            label: (
                                <span>
                                    <CloudOutlined style={{ marginRight: 4 }} />
                                    Saved Invoices
                                    {savedInvoices.length > 0 && (
                                        <Badge count={savedInvoices.length} size="small" style={{ marginLeft: 6 }} />
                                    )}
                                </span>
                            ),
                            children: (
                                <SavedInvoicesPanel
                                    savedInvoices={savedInvoices}
                                    savedLoading={savedLoading}
                                    filters={filters}
                                    months={months}
                                    loadSavedInvoices={loadSavedInvoices}
                                    handleLoadSaved={handleLoadSaved}
                                    handleDownloadPdf={handleDownloadPdf}
                                    setPrintData={setPrintData}
                                    handleDeleteSaved={handleDeleteSaved}
                                />
                            ),
                        },
                    ]}
                />

                {/* Hidden component for printing */}
                <div style={{ position: 'absolute', top: '-10000px', left: '-10000px' }}>
                    <InvoicePaper ref={hiddenPrintRef} data={printData} header={printData?.header} readOnly={true} />
                </div>

            </Card>
        </div>
    );
};

export default TaxInvoice;
